// useJobDetailsViewModel.ts
import { useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJobBrowsingController } from '../../jobBrowsing/controllers/useJobBrowsingController';
import {
  jobDetailsReducer,
  initialJobDetailsState,
  JobDetailsState,
} from '../model/JobDetailsState';

export const useJobDetailsViewModel = (jobId?: string): {
  state: JobDetailsState;
  handleApply: () => Promise<void>;
  clearError: () => void;
  removeToast: (id: number) => void;
  clearToasts: () => void;
} => {
  const navigate = useNavigate();
  const controller = useJobBrowsingController();
  const [state, dispatch] = useReducer(
    jobDetailsReducer,
    initialJobDetailsState
  );

  useEffect(() => {
    if (!jobId) return;

    const fetchJobDetails = async () => {
      dispatch({ type: 'FETCH_START' });

      try {
        const data = await controller.getJobDetails(jobId);
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch {
        dispatch({
          type: 'FETCH_ERROR',
          payload: 'Failed to load job details.',
        });
      }
    };

    fetchJobDetails();
  }, [jobId]);

  // Helper function to add toast
  const addToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    dispatch({ type: 'ADD_TOAST', payload: { message, type } });
    
    // Auto remove toast after 5 seconds
    const toastId = Date.now();
    setTimeout(() => {
      dispatch({ type: 'REMOVE_TOAST', payload: toastId });
    }, 5000);
  };

  const removeToast = (id: number) => {
    dispatch({ type: 'REMOVE_TOAST', payload: id });
  };

  const clearToasts = () => {
    dispatch({ type: 'CLEAR_TOASTS' });
  };

const handleApply = async () => {
  if (!state.job) return;

  if ((state.job as any)?.has_applied) {
    addToast('You have already applied for this job!', 'info');
    return;
  }

  // Navigate to application form
  navigate(`/jobs/browse/:jobId/apply`, { 
    state: { job: state.job } 
  });
};

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  return {
    state,
    handleApply,
    clearError,
    removeToast,
    clearToasts,
  };
};